type Tags = Record<string, boolean | string | number>;

type LoggerSeverity = "error" | "warning" | "fatal";

interface DatadogRumLike {
  addError: (error: Error, context?: Record<string, unknown>) => void;
  onReady?: (callback: () => void) => void;
}

declare global {
  interface Window {
    DD_RUM?: DatadogRumLike;
  }
}

export class AnalyticsModuleBootstrapError extends Error {
  tags: Tags = {};
  automatedEventName?: string;
  constructor(
    message?: string,
    options: {
      cause?: Error | undefined;
      tags?: Tags;
      automatedEventName?: string;
    } = { cause: undefined, tags: {} }
  ) {
    // @ts-ignore -- for some reason ts doesn't know that Error can take a cause
    super(message, { cause: options.cause });
    this.name = `AMBError`;
    const { cause, tags = {}, automatedEventName } = options;
    this.automatedEventName = automatedEventName;
    if (cause) {
      this.name = `${this.name}(${cause.name})`;
    }
    this.tag(tags);
  }

  tag(tags: Tags): AnalyticsModuleBootstrapError {
    this.tags = { ...this.tags, ...tags };
    return this;
  }
}

class Logger {
  prefix: string = "AMB";
  tags: Tags;
  isDatadogEnabled: boolean;
  owner: string;
  app: string;
  constructor(owner = "dcp", app = "analytics-module-bootstrap") {
    this.isDatadogEnabled = false;
    this.tags = {};
    this.prefix = `AMB`;
    this.owner = owner;
    this.app = app;
  }

  // Utility Methods
  private _addLogPrefix(args: any[], scope: string | string[] = []): any[] {
    const [message, ...rest] = args;
    const tags = this.tags;
    this.clearTags();

    const prefixParts = [
      this.prefix,
      ...(Array.isArray(scope) ? scope : [scope]),
    ];
    const prefix = `${prefixParts.join(":")}:`;

    if (message instanceof Error) {
      return [
        message instanceof AnalyticsModuleBootstrapError
          ? message.tag(tags)
          : new AnalyticsModuleBootstrapError(message?.message, {
              cause: message,
              tags,
            }),
        ...rest,
      ];
    } else if (typeof message === "string") {
      return [`${prefix} ${message}`, ...rest];
    }
    return [prefix, message, ...rest];
  }

  private _reportErrorToDatadog(
    error: Error,
    tags: Tags,
    severity: LoggerSeverity = "error"
  ): boolean {
    if (!this.isDatadogEnabled) {
      return false;
    }

    const rumClient = self.DD_RUM;
    if (typeof rumClient?.addError !== "function") {
      return false;
    }

    const automatedEventName =
      error instanceof AnalyticsModuleBootstrapError
        ? error.automatedEventName
        : undefined;
    const eventContext: Record<string, unknown> = {
      severity,
      lll_owner: this.owner,
      lll_team: this.owner,
      lll_app: this.app,
      team: this.owner,
      automatedEventName,
      tags: { ...tags },
    };

    const report = () => {
      rumClient.addError(error, eventContext);
    };

    try {
      if (typeof rumClient.onReady === "function") {
        rumClient.onReady(report);
      } else {
        report();
      }
      return true;
    } catch {
      // Preserve existing behavior: failures in telemetry should never throw.
      return false;
    }
  }

  // Tagging methods
  tag(tags: Tags): Logger {
    this.tags = { ...this.tags, ...tags };
    return this;
  }

  clearTags(): Logger {
    this.tags = {};
    return this;
  }

  // Core Logging Methods
  log(...args: [any, ...any]): void {
    if (getDebug()) {
      console.log(...this._addLogPrefix(args));
    }
  }

  logWithScope(scope: string | string[], ...args: [any, ...any]): void {
    if (getDebug(scope)) {
      console.log(...this._addLogPrefix(args, scope));
    }
  }

  info(...args: [any, ...any]): void {
    if (getDebug()) {
      console.info(...this._addLogPrefix(args));
    }
  }

  infoWithScope(scope: string | string[], ...args: [any, ...any]): void {
    if (getDebug(scope)) {
      console.info(...this._addLogPrefix(args, scope));
    }
  }

  error(...args: [any, ...any]): void {
    const pendingTags = { ...this.tags };
    let [error, ...rest] = this._addLogPrefix(args);
    if (typeof error === "string") {
      error = new AnalyticsModuleBootstrapError(error, {
        tags: pendingTags,
      });
    }

    const allTags: Tags =
      error instanceof AnalyticsModuleBootstrapError
        ? { ...pendingTags, ...(error.tags ?? {}) }
        : { ...pendingTags };

    if (this._reportErrorToDatadog(error, allTags, "error") === false) {
      console.error("(DD unavailable)", error, ...rest);
    }
  }

  warn(...args: [any, ...any]): void {
    if (getDebug()) {
      console.warn(...this._addLogPrefix(args));
    }
  }

  warnWithScope(scope: string | string[], ...args: [any, ...any]): void {
    if (getDebug(scope)) {
      console.warn(...this._addLogPrefix(args, scope));
    }
  }

  debug(...args: [any, ...any]): void {
    if (getDebug()) {
      console.debug(...this._addLogPrefix(args));
    }
  }

  debugWithScope(scope: string | string[], ...args: [any, ...any]): void {
    if (getDebug(scope)) {
      console.debug(...this._addLogPrefix(args, scope));
    }
  }
}

function parseDebugValue(
  debugValue: boolean | string | string[]
): boolean | string[] {
  if (typeof debugValue === "boolean" || Array.isArray(debugValue)) {
    // If the debug value is a boolean or an array, return it as is
    return debugValue;
  }
  if (debugValue === null || debugValue === undefined) {
    return false;
  }
  if (debugValue === "false") {
    return false;
  }
  if (debugValue.toLowerCase() === "true") {
    return true;
  }
  return debugValue.split(",").map((s) => s.trim());
}

export function getDebugValue(): boolean | string | string[] {
  return (
    self.analyticsDebug ??
    self.sessionStorage?.getItem("analyticsDebug") ??
    self.localStorage?.getItem("analyticsDebug") ??
    false
  );
}

export function getDebug(scope: string | string[] = ""): boolean {
  let debug: boolean | string[] = false;
  try {
    const debugValue = getDebugValue();

    // will be a bool if the debug value looks like a bool
    // otherwise it will be a string
    debug = parseDebugValue(debugValue);
  } catch {
    debug = false;
  } finally {
    try {
      self.analyticsDebug = debug;
    } catch (e) {
      console.warn(`${logger.prefix}: Could not set analyticsDebug`, e);
    }
  }
  if (!scope) {
    return !!debug;
  } else if (Array.isArray(debug)) {
    if (debug.includes("*")) {
      return true;
    } else if (typeof scope === "string") {
      return debug.includes(scope);
    } else if (Array.isArray(scope)) {
      return scope.some((s) => debug.includes(s));
    }
  }
  return false;
}

export const logger = new Logger();
